import { Badge, Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CloneDishesInterface, SelectedCategoryStateInterface } from "../interfaces/interface";
import BalanceImage from "../../../images/icones/balance.svg";
import menuImage from "../../../images/empty-menu-dish.png";
import { Link, useParams } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import useMenuItemsEditPage from "../hooks/useMenuItemsEditPage";
import { useManageMenuItemCategoryRelationMutation } from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import { CategoryPositionType } from "../../../utils/enums";
import { EditMenuService } from "../service/editMenuService";
import { ModalDelete, WithRoleAccess } from "@services/ui-components";
import MoveMenuItemModal from "../MoveMenuItemModal/MoveMenuItemModal";
import AddDishesModal from "../AddDishesModal/AddDishesModal";
import altDishImg from "../images/altDishImg.png";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../../Me-menu/utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../../Me-menu/utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../../Me-menu/components/LanguageVersionSelect/TranslationMissingDot";
import { getMenuStep } from "../helpers/helpers";
import { DisabledWithTooltip } from "@services/ui-components/src/DisabledWithTooltip/DisabledWithTooltip";
import CloneDishModal from "../DishCloneModal/CloneDishModal";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { cloneNameToDisplay } from "../../Me-menu/utils/cloneNameUtils";

type MenuByCategoryComponentProps = {
    selectedCategory: SelectedCategoryStateInterface;
    onStepChange?: (step: number) => void;
    onMenuStructureChanged: () => Promise<void>;
    canEditMenuContent?: boolean;
};

export type SelectedMenuTarget = {
    menuItemId: number;
    currentCategoryId: number;
    dishName: string;
    dishNameOrigin?: string | null;
};

const MenuByCategoryComponent: React.FC<MenuByCategoryComponentProps> = (props) => {
    const { t: translate } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );
    const translationTooltip = translate("Edit the language version");
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();
    const { id, branchId } = useParams();
    const basePath = process.env.REACT_APP_POS_URL;
    const [deleteMenuItemTarget, setDeleteMenuItemTarget] = useState<SelectedMenuTarget | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [moveMenuItemTarget, setMoveMenuItemTarget] = useState<SelectedMenuTarget | null>(null);
    const [cloneDishTarget, setCloneDishTarget] = useState<CloneDishesInterface | null>(null);
    const pathnameToEditDishPage = `/menu/dishes/create-dish/branch/${String(branchId).trim()}`;
    const [isAddDishModalOpen, setIsAddDishModalOpen] = useState<boolean>(false);
    const canEditMenuContentByRole = useRoleAccess(CompanyAccess.MenuCloneEdit);
    const canEditMenuContent = props.canEditMenuContent ?? canEditMenuContentByRole;
    const canCreateDish = useRoleAccess(CompanyAccess.MenuDishCreate);
    const canEditDish = useRoleAccess(CompanyAccess.MenuDishEdit);
    const canCloneDish = useRoleAccess(CompanyAccess.MenuDishClone);
    const selectedCategoryNameVm = buildTranslationTextVm({
        translatedValue: props.selectedCategory.categoryName,
        originValue: props.selectedCategory.categoryNameOrigin,
        isTranslationMode,
    });
    const selectedCategoryName = selectedCategoryNameVm.displayText;

    const { dishes, childCategories, currencySign, refetch, loading } = useMenuItemsEditPage({
        currentCompanyId,
        categoryId: props.selectedCategory.categoryId,
        childCategoryId: props.selectedCategory.childCategoryId,
        menuId: Number(id),
        localeVersion: selectedLangVersionCode,
    });

    const menuStep = getMenuStep(props.selectedCategory, dishes);

    const { onStepChange } = props;

    useEffect(() => {
        onStepChange?.(menuStep);
    }, [menuStep, onStepChange]);

    const [manageMenuItemCategoryRelation] = useManageMenuItemCategoryRelationMutation({
        context: {
            errorType: "local",
        },
    });

    const currentCategoryId = props.selectedCategory.childCategoryId
        ? props.selectedCategory.childCategoryId
        : props.selectedCategory.categoryId;

    const onMenuItemMove = async (menuItemId: number, categoryId: number, index: number, positionType: string) => {
        if (!canEditMenuContent) return;

        const indexForReq = index + 1;
        const positionIndex: number = positionType === CategoryPositionType.UP ? indexForReq - 1 : indexForReq + 1;

        await manageMenuItemCategoryRelation({
            variables: {
                companyId: Number(currentCompanyId),
                branchId: Number(branchId),
                menuId: Number(id),
                input: {
                    category: categoryId,
                    index: positionIndex,
                    menuItem: menuItemId,
                },
            },
        });
        await refetch();
    };

    const handleModalDeleteClose = () => {
        setDeleteMenuItemTarget(null);
    };

    const onMoveMenuItemToOtherCategory = useCallback(
        async (categoryId: number, subcategoryId: number | null) => {
            if (!canEditMenuContent || !moveMenuItemTarget) return;

            const destinationCategory = subcategoryId ? subcategoryId : categoryId;

            await manageMenuItemCategoryRelation({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    menuId: Number(id),
                    input: {
                        category: moveMenuItemTarget.currentCategoryId,
                        menuItem: moveMenuItemTarget.menuItemId,
                        destinationCategory: destinationCategory,
                    },
                },
            });

            await refetch();

            setMoveMenuItemTarget(null);
        },
        [
            canEditMenuContent,
            moveMenuItemTarget,
            currentCompanyId,
            branchId,
            id,
            refetch,
            manageMenuItemCategoryRelation,
        ]
    );

    const onAddDishesHandler = async (dishesToAdd: number[]) => {
        if (!canEditMenuContent) return;

        if (dishesToAdd.length === 1) {
            await manageMenuItemCategoryRelation({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    menuId: Number(id),
                    input: {
                        category: props.selectedCategory.childCategoryId
                            ? props.selectedCategory.childCategoryId
                            : props.selectedCategory.categoryId,
                        menuItem: dishesToAdd[0],
                    },
                },
            });
        } else {
            await manageMenuItemCategoryRelation({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    menuId: Number(id),
                    input: {
                        category: props.selectedCategory.childCategoryId
                            ? props.selectedCategory.childCategoryId
                            : props.selectedCategory.categoryId,
                        menuItems: dishesToAdd,
                    },
                },
            });
        }

        await refetch();
        await props.onMenuStructureChanged();
        setIsAddDishModalOpen(false);
    };

    const onCloseMoveModal = () => {
        setMoveMenuItemTarget(null);
    };

    const onCloseDishCloneHandler = () => {
        setCloneDishTarget(null);
    };

    const onSubmitCloneModalHandler = async () => {
        if (!canEditMenuContent) return;
        if (!canCloneDish) return;

        await refetch();
        toggleShowToast(translate("Saved"));
    };

    const isCategorySelected = !!props.selectedCategory?.categoryId || !!props.selectedCategory?.childCategoryId;

    return (
        <>
            <Col xl={7}>
                <Row className="justify-content-between align-items-start mb-4">
                    <Col lg={6} xl={4} xxl={5} className="mb-4 mb-lg-0">
                        <h3 className="fs-24 mb-0 text-break">
                            <span className="me-1">{selectedCategoryName}</span>
                            {selectedCategoryNameVm.isMissingTranslation ? (
                                <TranslationMissingDot tooltipText={translationTooltip} />
                            ) : null}
                        </h3>
                    </Col>
                    <Col lg={6} xl={8} xxl={6} className="d-flex justify-content-end">
                        {canEditMenuContent && (
                            <DisabledWithTooltip
                                disabled={!isCategorySelected}
                                tooltip={translate("First add categories to the menu")}
                                placement={"bottom"}
                            >
                                <Button
                                    className="btn-add-dish text-truncate py-1 px-2 me-3"
                                    variant="outline-primary"
                                    onClick={() => {
                                        if (!canEditMenuContent) return;
                                        setIsAddDishModalOpen(true);
                                    }}
                                >
                                    <i className="bi bi-plus-lg me-2 fs-16 lh-sm"></i>
                                    <span className="fs-7">{translate("Add dish")}</span>
                                </Button>
                            </DisabledWithTooltip>
                        )}
                        <WithRoleAccess permission={CompanyAccess.MenuDishCreate}>
                            <Link
                                to={`${pathnameToEditDishPage}`}
                                state={{
                                    menuContentInfo: {
                                        categoryId: props.selectedCategory.categoryId,
                                        childCategoryId: props.selectedCategory.childCategoryId,
                                        menuId: Number(id),
                                    },
                                }}
                                aria-disabled={!isCategorySelected}
                                className={`btn-create-dish text-truncate btn btn-primary py-1 px-2 ${
                                    !isCategorySelected ? "disabled opacity-50" : ""
                                }`}
                                onClick={(e) => {
                                    if (!isCategorySelected || !canCreateDish) e.preventDefault();
                                }}
                            >
                                <i className="bi bi-plus-lg me-2 fs-16 lh-sm"></i>
                                <span className="fs-7 fw-bold">{translate("Create dish")}</span>
                            </Link>
                        </WithRoleAccess>
                    </Col>
                </Row>

                {loading && <Spinner />}

                {!loading && (
                    <div className="d-flex flex-column gap-4 mb-4">
                        {dishes.length !== 0 ? (
                            dishes.map((item, index) => {
                                const nameVm = buildTranslationTextVm({
                                    translatedValue: item.name,
                                    originValue: item.nameOrigin,
                                    isTranslationMode,
                                });
                                const descriptionVm = buildTranslationTextVm({
                                    translatedValue: item.description,
                                    originValue: item.descriptionOrigin,
                                    isTranslationMode,
                                });
                                const compositionVm = buildTranslationTextVm({
                                    translatedValue: item.composition,
                                    originValue: item.compositionOrigin,
                                    isTranslationMode,
                                });

                                return (
                                    <div className="rounded border border-secondary p-3" key={item.id}>
                                        <div className="d-flex">
                                            <div className="d-flex flex-column flex-xxl-row flex-grow-1">
                                                {item.favicon ? (
                                                    <div className="img-menu me-4 flex-shrink-0">
                                                        <img
                                                            className="object-fit-cover rounded-1"
                                                            alt="Dish"
                                                            src={`${basePath}/${item.favicon}`}
                                                            width={110}
                                                            height={110}
                                                        ></img>
                                                    </div>
                                                ) : (
                                                    <div className="img-menu me-4 flex-shrink-0">
                                                        <img
                                                            className="object-fit-cover rounded-1"
                                                            alt="Dish"
                                                            src={altDishImg}
                                                            width={110}
                                                            height={110}
                                                        ></img>
                                                    </div>
                                                )}
                                                <div className="my-3 my-xxl-0 pe-4 flex-grow-1">
                                                    <div className="d-flex gap-2 align-items-start">
                                                        <h4 className="fs-16 mb-0">
                                                            <span className="me-1">
                                                                {cloneNameToDisplay(nameVm.displayText, translate)}
                                                            </span>
                                                        </h4>
                                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                                            {item.markers.length > 0 && (
                                                                <div className={"d-flex align-items-center gap-2"}>
                                                                    {item.markers.map((marker) => {
                                                                        return (
                                                                            <img
                                                                                key={`${marker.id}-markerName-${marker.name}`}
                                                                                src={`${basePath}/${marker.favicon}`}
                                                                                alt={marker.name}
                                                                            />
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                            {item.allergens.length > 0 && (
                                                                <div className="d-flex align-items-center gap-1 ms-2">
                                                                    <i className="bi bi-exclamation-triangle text-danger d-flex" />
                                                                    <span className="fs-7 me-3 line-height-normal">
                                                                        {item.allergens.length}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            <div className={"d-flex align-items-center gap-2"}>
                                                                <WithRoleAccess permission={CompanyAccess.MenuDishEdit}>
                                                                    <OverlayTrigger
                                                                        placement="bottom"
                                                                        overlay={<Tooltip>{translate("Edit")}</Tooltip>}
                                                                    >
                                                                        <Link
                                                                            to={`/menu/update-dish/dish/${item.id}/branch/${branchId}`}
                                                                            state={{
                                                                                menuId: id,
                                                                            }}
                                                                            className="btn btn-violet-light d-flex justify-content-center align-items-center text-primary px-1 py-0 rounded-1"
                                                                            onClick={(e) => {
                                                                                if (!canEditDish) e.preventDefault();
                                                                                e.stopPropagation();
                                                                            }}
                                                                        >
                                                                            <i className="bi bi-pencil fs-7"></i>
                                                                        </Link>
                                                                    </OverlayTrigger>
                                                                </WithRoleAccess>
                                                                {canEditMenuContent && (
                                                                    <OverlayTrigger
                                                                        placement="bottom"
                                                                        overlay={
                                                                            <Tooltip>
                                                                                {translate("Remove from menu")}
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <Button
                                                                            variant="violet-light"
                                                                            className="d-flex justify-content-center align-items-center text-primary px-1 py-0 rounded-1"
                                                                            onClick={() => {
                                                                                if (!canEditMenuContent) return;
                                                                                setDeleteMenuItemTarget({
                                                                                    menuItemId: item.id,
                                                                                    currentCategoryId:
                                                                                        currentCategoryId,
                                                                                    dishName: item.name,
                                                                                    dishNameOrigin: item.nameOrigin,
                                                                                });
                                                                            }}
                                                                        >
                                                                            <i className="bi bi-trash fs-7"></i>
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                )}
                                                                {canEditMenuContent && (
                                                                    <OverlayTrigger
                                                                        placement="bottom"
                                                                        overlay={
                                                                            <Tooltip>{translate("Move dish")}</Tooltip>
                                                                        }
                                                                    >
                                                                        <Button
                                                                            variant="violet-light"
                                                                            className="d-flex justify-content-center align-items-center text-primary px-1 py-0 rounded-1"
                                                                            onClick={() => {
                                                                                if (!canEditMenuContent) return;
                                                                                setMoveMenuItemTarget({
                                                                                    menuItemId: item.id,
                                                                                    currentCategoryId:
                                                                                        currentCategoryId,
                                                                                    dishName: item.name,
                                                                                    dishNameOrigin: item.nameOrigin,
                                                                                });
                                                                            }}
                                                                        >
                                                                            <i className="bi bi-box-arrow-right fs-7"></i>
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                )}
                                                                <WithRoleAccess
                                                                    permission={CompanyAccess.MenuDishClone}
                                                                >
                                                                    <OverlayTrigger
                                                                        placement="bottom"
                                                                        overlay={
                                                                            <Tooltip>{translate("Clone")}</Tooltip>
                                                                        }
                                                                    >
                                                                        <Button
                                                                            variant="violet-light"
                                                                            className="d-flex justify-content-center align-items-center text-primary px-1 py-0 rounded-1"
                                                                            onClick={() => {
                                                                                if (!canEditMenuContent) return;
                                                                                if (!canCloneDish) return;
                                                                                setCloneDishTarget({
                                                                                    cloneItem: {
                                                                                        id: item.id,
                                                                                        name: item.name,
                                                                                        originalName: item.nameOrigin,
                                                                                    },
                                                                                    categoryInfo: {
                                                                                        categoryId:
                                                                                            props.selectedCategory
                                                                                                .categoryId,
                                                                                        subcategoryId:
                                                                                            props.selectedCategory
                                                                                                .childCategoryId,
                                                                                    },
                                                                                });
                                                                            }}
                                                                        >
                                                                            <i className="bi bi-stickies fs-7"></i>
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                </WithRoleAccess>
                                                                {nameVm.isMissingTranslation ? (
                                                                    <TranslationMissingDot
                                                                        tooltipText={translationTooltip}
                                                                    />
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {descriptionVm.displayText ? (
                                                        <p className="fs-7 text-grey mt-1">
                                                            <span className="me-1">{descriptionVm.displayText}</span>
                                                            {descriptionVm.isMissingTranslation ? (
                                                                <TranslationMissingDot
                                                                    tooltipText={translationTooltip}
                                                                />
                                                            ) : null}
                                                        </p>
                                                    ) : null}
                                                    {compositionVm.displayText ? (
                                                        <p className="fs-7 text-line-clamp-2">
                                                            <span className="me-1">{compositionVm.displayText}</span>
                                                            {compositionVm.isMissingTranslation ? (
                                                                <TranslationMissingDot
                                                                    tooltipText={translationTooltip}
                                                                />
                                                            ) : null}
                                                        </p>
                                                    ) : null}
                                                    {item.supplements.length > 0 && (
                                                        <div>
                                                            <h5 className="fs-7 fw-bold mb-2">
                                                                {translate("Additives")}
                                                            </h5>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {item.supplements.map((elem, index) => {
                                                                    const supplementNameVm = buildTranslationTextVm({
                                                                        translatedValue: elem.groupName,
                                                                        originValue: elem.groupNameOrigin,
                                                                        isTranslationMode,
                                                                    });

                                                                    return (
                                                                        <Badge
                                                                            key={`${elem.id}-supp-${index}`}
                                                                            bg="additional-grey"
                                                                            className="rounded-1 p-2 fs-7 text-dark d-flex align-items-center text-wrap text-start"
                                                                        >
                                                                            <span className="me-1">
                                                                                {supplementNameVm.displayText}
                                                                            </span>
                                                                            {supplementNameVm.isMissingTranslation ? (
                                                                                <TranslationMissingDot
                                                                                    tooltipText={translationTooltip}
                                                                                />
                                                                            ) : null}
                                                                        </Badge>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-shrink-0 me-3">
                                                    <p className="fs-16 fw-bold text-xxl-nowrap text-start text-xxl-end mb-2">
                                                        {EditMenuService.portionsValueView(item.portions, "price")}{" "}
                                                        {currencySign}
                                                    </p>{" "}
                                                    {item.cookingTime && (
                                                        <div className="d-flex align-items-center justify-content-start justify-content-xxl-center py-1">
                                                            <i className="bi bi-clock-history text-grey fs-16 me-2"></i>
                                                            <span className="fs-7">{item.cookingTime}</span>
                                                        </div>
                                                    )}
                                                    <div className="d-flex align-items-center justify-content-start justify-content-xxl-center py-1">
                                                        <div className="me-2">
                                                            <BalanceImage />
                                                        </div>
                                                        <span className="fs-7">
                                                            {EditMenuService.portionsValueView(item.portions, "volume")}{" "}
                                                            {item.unitName}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex flex-column justify-content-between align-items-stretch ps-3 ms-auto border-start border-secondary">
                                                <Button
                                                    variant="link"
                                                    className="text-dark p-0"
                                                    disabled={index === 0 || !canEditMenuContent}
                                                    onClick={async () => {
                                                        await onMenuItemMove(
                                                            item.id,
                                                            currentCategoryId,
                                                            index,
                                                            CategoryPositionType.UP
                                                        );
                                                    }}
                                                >
                                                    <i className="bi bi-chevron-up"></i>
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    className="text-dark p-0"
                                                    disabled={dishes.length - 1 === index || !canEditMenuContent}
                                                    onClick={async () => {
                                                        await onMenuItemMove(
                                                            item.id,
                                                            currentCategoryId,
                                                            index,
                                                            CategoryPositionType.DOWN
                                                        );
                                                    }}
                                                >
                                                    <i className="bi bi-chevron-down"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="rounded border border-additional-grey p-3">
                                <img src={menuImage} className="img-fluid" alt="Menu card empty" />
                            </div>
                        )}
                        {childCategories.length !== 0 &&
                            childCategories.map((childCategory) => {
                                const childCategoryNameVm = buildTranslationTextVm({
                                    translatedValue: childCategory.name,
                                    originValue: childCategory.nameOrigin,
                                    isTranslationMode,
                                });

                                return (
                                    <div key={childCategory.id} className="d-flex flex-column gap-3">
                                        <div>
                                            <h4 className="fs-16 mb-0">
                                                <span className="me-1">{childCategoryNameVm.displayText}</span>
                                                {childCategoryNameVm.isMissingTranslation ? (
                                                    <TranslationMissingDot tooltipText={translationTooltip} />
                                                ) : null}
                                            </h4>
                                        </div>

                                        {childCategory.dishes.length > 0 ? (
                                            childCategory.dishes.map((item, index) => {
                                                const nameVm = buildTranslationTextVm({
                                                    translatedValue: item.name,
                                                    originValue: item.nameOrigin,
                                                    isTranslationMode,
                                                });
                                                const descriptionVm = buildTranslationTextVm({
                                                    translatedValue: item.description,
                                                    originValue: item.descriptionOrigin,
                                                    isTranslationMode,
                                                });
                                                const compositionVm = buildTranslationTextVm({
                                                    translatedValue: item.composition,
                                                    originValue: item.compositionOrigin,
                                                    isTranslationMode,
                                                });

                                                return (
                                                    <div className="rounded border border-secondary p-3" key={item.id}>
                                                        <div className="d-flex">
                                                            <div className="d-flex flex-column flex-xxl-row flex-grow-1">
                                                                {item.favicon ? (
                                                                    <div className="img-menu me-4 flex-shrink-0">
                                                                        <img
                                                                            className="object-fit-cover rounded-1"
                                                                            alt="Dish"
                                                                            src={`${basePath}/${item.favicon}`}
                                                                            width={110}
                                                                            height={110}
                                                                        ></img>
                                                                    </div>
                                                                ) : (
                                                                    <div className="img-menu me-4 flex-shrink-0">
                                                                        <img
                                                                            className="object-fit-cover rounded-1"
                                                                            alt="Dish"
                                                                            src={altDishImg}
                                                                            width={110}
                                                                            height={110}
                                                                        ></img>
                                                                    </div>
                                                                )}

                                                                <div className="my-3 my-xxl-0 pe-4 flex-grow-1">
                                                                    <div className="d-flex gap-2 align-items-start">
                                                                        <h4 className="fs-16 mb-0">
                                                                            <span className="me-1">
                                                                                {cloneNameToDisplay(
                                                                                    nameVm.displayText,
                                                                                    translate
                                                                                )}
                                                                            </span>
                                                                        </h4>

                                                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                                                            {item.markers.length > 0 && (
                                                                                <div
                                                                                    className={
                                                                                        "d-flex align-items-center gap-2"
                                                                                    }
                                                                                >
                                                                                    {item.markers.map((marker) => {
                                                                                        return (
                                                                                            <img
                                                                                                key={`${marker.id}-markerName-${marker.name}`}
                                                                                                src={`${basePath}/${marker.favicon}`}
                                                                                                alt={marker.name}
                                                                                            />
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            )}

                                                                            {item.allergens.length > 0 && (
                                                                                <div className="d-flex align-items-center gap-1 ms-2">
                                                                                    <i className="bi bi-exclamation-triangle text-danger d-flex" />
                                                                                    <span className="fs-7 me-3 line-height-normal">
                                                                                        {item.allergens.length}
                                                                                    </span>
                                                                                </div>
                                                                            )}

                                                                            <div
                                                                                className={
                                                                                    "d-flex align-items-center gap-2"
                                                                                }
                                                                            >
                                                                                <WithRoleAccess
                                                                                    permission={
                                                                                        CompanyAccess.MenuDishEdit
                                                                                    }
                                                                                >
                                                                                    <OverlayTrigger
                                                                                        placement="bottom"
                                                                                        overlay={
                                                                                            <Tooltip>
                                                                                                {translate("Edit")}
                                                                                            </Tooltip>
                                                                                        }
                                                                                    >
                                                                                        <Link
                                                                                            to={`/menu/update-dish/dish/${item.id}/branch/${branchId}`}
                                                                                            state={{
                                                                                                menuId: id,
                                                                                            }}
                                                                                            className="btn btn-violet-light d-flex justify-content-center align-items-center text-primary px-1 py-0 rounded-1"
                                                                                            onClick={(e) => {
                                                                                                if (!canEditDish) {
                                                                                                    e.preventDefault();
                                                                                                }
                                                                                                e.stopPropagation();
                                                                                            }}
                                                                                        >
                                                                                            <i className="bi bi-pencil fs-7"></i>
                                                                                        </Link>
                                                                                    </OverlayTrigger>
                                                                                </WithRoleAccess>

                                                                                {canEditMenuContent && (
                                                                                    <OverlayTrigger
                                                                                        placement="bottom"
                                                                                        overlay={
                                                                                            <Tooltip>
                                                                                                {translate(
                                                                                                    "Remove from menu"
                                                                                                )}
                                                                                            </Tooltip>
                                                                                        }
                                                                                    >
                                                                                        <Button
                                                                                            variant="violet-light"
                                                                                            className="d-flex justify-content-center align-items-center text-primary px-1 py-0 rounded-1"
                                                                                            onClick={() => {
                                                                                                if (!canEditMenuContent)
                                                                                                    return;
                                                                                                setDeleteMenuItemTarget(
                                                                                                    {
                                                                                                        menuItemId:
                                                                                                            item.id,
                                                                                                        currentCategoryId:
                                                                                                            childCategory.id,
                                                                                                        dishName:
                                                                                                            item.name,
                                                                                                        dishNameOrigin:
                                                                                                            item.nameOrigin,
                                                                                                    }
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            <i className="bi bi-trash fs-7"></i>
                                                                                        </Button>
                                                                                    </OverlayTrigger>
                                                                                )}

                                                                                {canEditMenuContent && (
                                                                                    <OverlayTrigger
                                                                                        placement="bottom"
                                                                                        overlay={
                                                                                            <Tooltip>
                                                                                                {translate("Move dish")}
                                                                                            </Tooltip>
                                                                                        }
                                                                                    >
                                                                                        <Button
                                                                                            variant="violet-light"
                                                                                            className="d-flex justify-content-center align-items-center text-primary px-1 py-0 rounded-1"
                                                                                            onClick={() => {
                                                                                                if (!canEditMenuContent)
                                                                                                    return;
                                                                                                setMoveMenuItemTarget({
                                                                                                    menuItemId: item.id,
                                                                                                    dishName: item.name,
                                                                                                    currentCategoryId:
                                                                                                        childCategory.id,
                                                                                                    dishNameOrigin:
                                                                                                        item.nameOrigin,
                                                                                                });
                                                                                            }}
                                                                                        >
                                                                                            <i className="bi bi-box-arrow-right fs-7"></i>
                                                                                        </Button>
                                                                                    </OverlayTrigger>
                                                                                )}

                                                                                <WithRoleAccess
                                                                                    permission={
                                                                                        CompanyAccess.MenuDishClone
                                                                                    }
                                                                                >
                                                                                    <OverlayTrigger
                                                                                        placement="bottom"
                                                                                        overlay={
                                                                                            <Tooltip>
                                                                                                {translate("Clone")}
                                                                                            </Tooltip>
                                                                                        }
                                                                                    >
                                                                                        <Button
                                                                                            variant="violet-light"
                                                                                            className="d-flex justify-content-center align-items-center text-primary px-1 py-0 rounded-1"
                                                                                            onClick={() => {
                                                                                                if (!canEditMenuContent)
                                                                                                    return;
                                                                                                if (!canCloneDish)
                                                                                                    return;
                                                                                                const selectedCategory =
                                                                                                    props
                                                                                                        .selectedCategory
                                                                                                        .childCategoryId
                                                                                                        ? props
                                                                                                              .selectedCategory
                                                                                                              .childCategoryId
                                                                                                        : props
                                                                                                              .selectedCategory
                                                                                                              .categoryId;

                                                                                                setCloneDishTarget({
                                                                                                    cloneItem: {
                                                                                                        id: item.id,
                                                                                                        name: item.name,
                                                                                                        originalName:
                                                                                                            item.nameOrigin,
                                                                                                    },
                                                                                                    categoryInfo: {
                                                                                                        categoryId:
                                                                                                            selectedCategory,
                                                                                                        subcategoryId:
                                                                                                            childCategory.id,
                                                                                                    },
                                                                                                });
                                                                                            }}
                                                                                        >
                                                                                            <i className="bi bi-stickies fs-7"></i>
                                                                                        </Button>
                                                                                    </OverlayTrigger>
                                                                                </WithRoleAccess>

                                                                                {nameVm.isMissingTranslation ? (
                                                                                    <TranslationMissingDot
                                                                                        tooltipText={translationTooltip}
                                                                                    />
                                                                                ) : null}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {descriptionVm.displayText ? (
                                                                        <p className="fs-7 text-grey mt-1">
                                                                            <span className="me-1">
                                                                                {descriptionVm.displayText}
                                                                            </span>
                                                                            {descriptionVm.isMissingTranslation ? (
                                                                                <TranslationMissingDot
                                                                                    tooltipText={translationTooltip}
                                                                                />
                                                                            ) : null}
                                                                        </p>
                                                                    ) : null}

                                                                    {compositionVm.displayText ? (
                                                                        <p className="fs-7 text-line-clamp-2">
                                                                            <span className="me-1">
                                                                                {compositionVm.displayText}
                                                                            </span>
                                                                            {compositionVm.isMissingTranslation ? (
                                                                                <TranslationMissingDot
                                                                                    tooltipText={translationTooltip}
                                                                                />
                                                                            ) : null}
                                                                        </p>
                                                                    ) : null}

                                                                    {item.supplements.length > 0 && (
                                                                        <div>
                                                                            <h5 className="fs-7 fw-bold mb-2">
                                                                                {translate("Additives")}
                                                                            </h5>
                                                                            <div className="d-flex flex-wrap gap-2">
                                                                                {item.supplements.map((elem, index) => {
                                                                                    const supplementNameVm =
                                                                                        buildTranslationTextVm({
                                                                                            translatedValue:
                                                                                                elem.groupName,
                                                                                            originValue:
                                                                                                elem.groupNameOrigin,
                                                                                            isTranslationMode,
                                                                                        });

                                                                                    return (
                                                                                        <Badge
                                                                                            key={`${elem.id}-supp-${index}`}
                                                                                            bg="additional-grey"
                                                                                            className="rounded-1 p-2 fs-7 text-dark d-flex align-items-center text-wrap text-start"
                                                                                        >
                                                                                            <span className="me-1">
                                                                                                {
                                                                                                    supplementNameVm.displayText
                                                                                                }
                                                                                            </span>
                                                                                            {supplementNameVm.isMissingTranslation ? (
                                                                                                <TranslationMissingDot
                                                                                                    tooltipText={
                                                                                                        translationTooltip
                                                                                                    }
                                                                                                />
                                                                                            ) : null}
                                                                                        </Badge>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="flex-shrink-0 me-3">
                                                                    <p className="fs-16 fw-bold text-xxl-nowrap text-start text-xxl-end mb-2">
                                                                        {EditMenuService.portionsValueView(
                                                                            item.portions,
                                                                            "price"
                                                                        )}{" "}
                                                                        {currencySign}
                                                                    </p>

                                                                    {item.cookingTime && (
                                                                        <div className="d-flex align-items-center justify-content-start justify-content-xxl-center py-1">
                                                                            <i className="bi bi-clock-history text-grey fs-16 me-2"></i>
                                                                            <span className="fs-7">
                                                                                {item.cookingTime}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    <div className="d-flex align-items-center justify-content-start justify-content-xxl-center py-1">
                                                                        <div className="me-2">
                                                                            <BalanceImage />
                                                                        </div>
                                                                        <span className="fs-7">
                                                                            {EditMenuService.portionsValueView(
                                                                                item.portions,
                                                                                "volume"
                                                                            )}{" "}
                                                                            {item.unitName}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="d-flex flex-column justify-content-between align-items-stretch ps-3 ms-auto border-start border-secondary">
                                                                <Button
                                                                    variant="link"
                                                                    className="text-dark p-0"
                                                                    disabled={index === 0 || !canEditMenuContent}
                                                                    onClick={async () => {
                                                                        await onMenuItemMove(
                                                                            item.id,
                                                                            childCategory.id,
                                                                            index,
                                                                            CategoryPositionType.UP
                                                                        );
                                                                    }}
                                                                >
                                                                    <i className="bi bi-chevron-up"></i>
                                                                </Button>

                                                                <Button
                                                                    variant="link"
                                                                    className="text-dark p-0"
                                                                    disabled={
                                                                        childCategory.dishes.length - 1 === index ||
                                                                        !canEditMenuContent
                                                                    }
                                                                    onClick={async () => {
                                                                        await onMenuItemMove(
                                                                            item.id,
                                                                            childCategory.id,
                                                                            index,
                                                                            CategoryPositionType.DOWN
                                                                        );
                                                                    }}
                                                                >
                                                                    <i className="bi bi-chevron-down"></i>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="rounded border border-additional-grey p-3">
                                                <img src={menuImage} className="img-fluid" alt="Menu card empty" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        <Col xxl={4} className="mx-auto">
                            {dishes.length !== 0 ? (
                                <Link
                                    to={`/menu/${id}/preview/branch/${branchId}`}
                                    className="btn btn-primary fw-bold w-100"
                                >
                                    {translate("View")}
                                </Link>
                            ) : (
                                <>
                                    {dishes.length !== 0 ? (
                                        <Link
                                            to={`/menu/${id}/preview/branch/${branchId}`}
                                            className="btn btn-primary fw-bold w-100"
                                        >
                                            {translate("View")}
                                        </Link>
                                    ) : (
                                        <WithRoleAccess permission={CompanyAccess.MenuDishCreate}>
                                            <Link
                                                to={`${pathnameToEditDishPage}`}
                                                state={{
                                                    menuContentInfo: {
                                                        categoryId: props.selectedCategory.categoryId,
                                                        childCategoryId: props.selectedCategory.childCategoryId,
                                                        menuId: Number(id),
                                                    },
                                                }}
                                                aria-disabled={!isCategorySelected}
                                                className={`btn-create-dish text-truncate btn btn-primary py-1 px-2 ${
                                                    !isCategorySelected ? "disabled opacity-50" : ""
                                                }`}
                                                onClick={(e) => {
                                                    if (!isCategorySelected || !canCreateDish) e.preventDefault();
                                                }}
                                            >
                                                <i className="bi bi-plus-lg me-2 fs-16 lh-sm"></i>
                                                <span className="fs-7 fw-bold">{translate("Create dish")}</span>
                                            </Link>
                                        </WithRoleAccess>
                                    )}
                                </>
                            )}
                        </Col>
                    </div>
                )}
            </Col>

            <ModalDelete
                show={!!deleteMenuItemTarget}
                handleClose={handleModalDeleteClose}
                title={(() => {
                    const nameVm = buildTranslationTextVm({
                        translatedValue: deleteMenuItemTarget?.dishName,
                        originValue: deleteMenuItemTarget?.dishNameOrigin,
                        isTranslationMode,
                    });

                    return (
                        <span className="d-inline-flex align-items-center">
                            <span className="me-1">
                                {translate("deleteDishTitle", {
                                    name: cloneNameToDisplay(nameVm.displayText, translate),
                                })}
                            </span>
                            {nameVm.isMissingTranslation ? (
                                <TranslationMissingDot tooltipText={translationTooltip} />
                            ) : null}
                        </span>
                    );
                })()}
                body={translate("Do you really want to delete this dish from the menu?")}
                buttons={[
                    {
                        text: translate("cancel"),
                        onClick: handleModalDeleteClose,
                        variant: "btn btn-outline-violet border-primary text-primary fw-normal",
                        className: "d-flex justify-content-center align-items-center w-100",
                    },
                    {
                        text: translate("delete"),
                        onClick: async () => {
                            if (!canEditMenuContent) return;
                            if (isDeleting) return;
                            setIsDeleting(true);
                            await manageMenuItemCategoryRelation({
                                variables: {
                                    companyId: Number(currentCompanyId),
                                    branchId: Number(branchId),
                                    menuId: Number(id),
                                    input: {
                                        category: deleteMenuItemTarget.currentCategoryId,
                                        deleted: true,
                                        menuItem: deleteMenuItemTarget.menuItemId,
                                    },
                                },
                            });

                            await refetch();
                            await props.onMenuStructureChanged();
                            setDeleteMenuItemTarget(null);
                            setIsDeleting(false);
                        },
                        variant: "primary",
                        className: "w-100 fw-bold",
                    },
                ]}
            />

            <MoveMenuItemModal
                isOpen={!!moveMenuItemTarget}
                menuId={Number(id)}
                menuItemTarget={moveMenuItemTarget}
                moveCategoryHandler={onMoveMenuItemToOtherCategory}
                onCloseMoveModal={onCloseMoveModal}
                canEditMenuContent={canEditMenuContent}
            />

            <AddDishesModal
                branchId={Number(branchId)}
                menuId={Number(id)}
                selectedCategory={props.selectedCategory}
                isOpen={isAddDishModalOpen}
                onClose={() => {
                    setIsAddDishModalOpen(false);
                }}
                onAddDishesHandler={onAddDishesHandler}
                canEditMenuContent={canEditMenuContent}
                canEditDish={canEditDish}
            />
            <CloneDishModal
                isOpen={Boolean(cloneDishTarget)}
                cloneDishModalInfo={cloneDishTarget}
                menuId={Number(id)}
                branchId={Number(branchId)}
                onClose={onCloseDishCloneHandler}
                onCloneSubmit={onSubmitCloneModalHandler}
                canCloneDish={canCloneDish}
                canEditMenuContent={canEditMenuContent}
            />
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default React.memo(MenuByCategoryComponent);
